/**
 * Status type for toast notifications determining visual styling
 */
export type ToastStatus = 'success' | 'warning' | 'error' | 'info';

/**
 * Position where toasts appear on screen
 */
export type ToastPosition = 'top' | 'top-right' | 'top-left' | 'bottom' | 'bottom-right' | 'bottom-left';

/**
 * An optional call-to-action button rendered inside a toast notification.
 */
export interface ToastAction {
  /** Visible label for the action button */
  label: string;
  /** Callback invoked when the action button is clicked */
  onClick: () => void;
}

/**
 * Configuration for creating a toast notification
 */
export interface ToastConfig {
  /** Optional title displayed above the message */
  title?: string;
  /** Main message content */
  message: string;
  /** Visual status determining color and icon */
  status: ToastStatus;
  /** Duration in milliseconds before auto-dismiss. 0 = no auto-dismiss. Default: 5000 */
  duration?: number;
  /** Whether the toast can be manually dismissed. Default: true */
  dismissible?: boolean;
  /** Optional call-to-action link shown below the message */
  action?: ToastAction;
}

/**
 * A toast notification instance with generated id and timestamp.
 * Duration and dismissible are required (have defaults applied).
 */
export interface Toast extends Required<Pick<ToastConfig, 'duration' | 'dismissible'>>, Omit<ToastConfig, 'duration' | 'dismissible'> {
  /** Unique identifier for the toast */
  id: string;
  /** Timestamp when the toast was created */
  createdAt: number;
}
